import { createNativePushSubscription } from '@kudos/http-client'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  isKudosMobileShell,
  registerKudosNativeBridgeListener,
  requestDeviceInfoFromShell,
  unregisterKudosNativeBridgeListener,
  type DeviceInfoPayload,
} from './nativeBridge'
import { useAuthStore } from './stores/useAuthStore'

/**
 * Bootstraps the Kudos native ↔ web bridge when running inside the Capacitor shell.
 *
 * - Registers the `messageFromNative` listener once per authenticated session.
 * - On `push.setToken`: registers the FCM/APNs token with the backend when the member
 *   is authenticated (de-duplicated to avoid redundant API calls when the shell
 *   resends the same token). Pre-login events are skipped until login.
 * - On `push.notificationTap`: navigates to the deep-link path inside the SPA, or
 *   opens external API URLs in the system browser.
 * - On unmount: removes the listener so a future re-mount (e.g. re-login) picks up
 *   fresh navigate/auth refs.
 *
 * Call this hook once, high up in the layout (e.g. RootLayout).
 *
 * @returns An object containing the native shell detection flag and the device info
 *   fetched on mount (both currently unused by callers — see UNUSED note below).
 */
// UNUSED: the deviceInfo and isNative return values are not consumed by any caller today.
// Retained so adding a consumer is non-breaking.
export const useNativeBridge = (): { isNative: boolean; deviceInfo: DeviceInfoPayload | null } => {
  const navigate = useNavigate()
  const isNative = isKudosMobileShell()
  const userId = useAuthStore(s => s.userId)
  const tenantId = useAuthStore(s => s.tenantId)
  const deviceInfoRef = useRef<DeviceInfoPayload | null>(null)
  const pushTokenRef = useRef<string | null>(null)

  // RootLayout (and this hook) stays mounted across logout -> login on the same device.
  // Without this reset, the dedupe guard in onPushToken would treat the next account's
  // identical OS token as "already registered" and skip createNativePushSubscription,
  // so the new member would never receive push notifications.
  useEffect(() => {
    pushTokenRef.current = null
  }, [userId, tenantId])

  useEffect(() => {
    if (!isNative) return

    // UNUSED: requestDeviceInfoFromShell result is informational only.
    // The deviceInfoRef is exposed in the return value but no caller reads it today.
    void requestDeviceInfoFromShell()
      .then(info => {
        deviceInfoRef.current = info
      })
      .catch(() => {
        // Non-fatal — device info is informational only
      })

    registerKudosNativeBridgeListener({
      /**
       * Handles a push.setToken event from the shell — the OS has issued or refreshed
       * an FCM/APNs token. Registers the token with the backend, skipping duplicates
       * to avoid unnecessary API calls when the shell resends the same token.
       *
       * @param token - The FCM (Android) or APNs (iOS) device token string.
       * @param platform - The platform identifier returned by the shell.
       */
      onPushToken: ({ token, platform }) => {
        // Registration is member-scoped: a pre-login token would only 401. Skip without
        // recording it in pushTokenRef so the same token still registers once the user
        // logs in and the shell re-emits it (or the OS rotates the token). The
        // userId/tenantId effect also resets the dedupe ref on login.
        const { userId, tenantId } = useAuthStore.getState()
        if (!userId || !tenantId) return

        // De-duplicate: skip if the same token was already registered this session.
        if (pushTokenRef.current === token) return
        pushTokenRef.current = token

        void createNativePushSubscription({
          token,
          platform: platform ?? 'unknown',
        })
          .then(() => {
            // Token registered successfully
          })
          .catch(() => {
            // Reset ref so we retry on the next push.setToken event
            pushTokenRef.current = null
          })
      },

      /**
       * Handles a push.notificationTap event from the shell — the user tapped a
       * push notification. Navigates to the target path inside the SPA, or opens
       * external API URLs in the system browser.
       *
       * @param path - The navigation target: a SPA path (type 'client') or a full URL (type 'api').
       * @param type - 'client' navigates inside the SPA; 'api' opens in the system browser.
       */
      onPushNotificationTap: ({ path, type }) => {
        if (!path) return
        if (type === 'api') {
          // Full API URL — open in the system browser; the server will redirect to a frontend route.
          // Do not use navigate() here: React Router cannot handle external URLs.
          window.open(path, '_blank', 'noopener,noreferrer')
        } else {
          // type === 'client' (default): frontend path — navigate within the SPA.
          navigate(path)
        }
      },
    })

    return () => {
      // Unregister so a future login (re-mount) gets a fresh handler with the current
      // navigate ref. Token deletion is intentionally omitted — see the NOTE in this
      // hook's JSDoc for the full explanation.
      unregisterKudosNativeBridgeListener()
    }
  }, [isNative, navigate])

  return { isNative, deviceInfo: deviceInfoRef.current }
}
